import Util from '../../shared/util/util';
import DirectApi from '../../../directApi';

export const INVOICE_INTEGRATIONS = {
	XERO: 'XERO',
	ECONOMIC: 'ECONOMIC',
	QUICKBOOKS: 'QUICKBOOKS',
};

export type Contact = {
	id: string;
	name: string;
	address?: string;
	taxId?: string;
	integrationInstanceId?: string;
	integration: string;
};

export type EnabledInvoiceIntegrations = {
	readonly newXeroEnabled?: boolean | null;
	readonly economicEnabled?: boolean | null;
	readonly quickbooksEnabled?: boolean | null;
};

export class ContactsFetchError extends Error {
	readonly integration: string;
	readonly correlationId?: string;

	constructor(integration: string, correlationId?: string) {
		super(`Failed to fetch ${integration} contacts`);
		this.name = 'ContactsFetchError';
		this.integration = integration;
		this.correlationId = correlationId;
	}
}

type IntegrationCustomer = {
	id: string | number;
	name: string;
	taxId?: string | number;
	address?: string;
	integrationInstanceId?: string;
};

const defaultContactsMapper = (customers: IntegrationCustomer[], integrationName: string): Contact[] =>
	customers
		.sort((a, b) => Util.sortAlphabetically(a.name, b.name))
		.map(customer => ({
			id: String(customer.id),
			name: customer.name,
			// '' rather than undefined: bill_to.tax_id is NOT NULL, and the Bill To modals send this as-is.
			taxId: customer.taxId == null ? '' : String(customer.taxId),
			address: customer.address,
			integrationInstanceId: customer.integrationInstanceId,
			integration: integrationName,
		}));

const fetchContacts = (endpoint: string, integration: string): Promise<Contact[]> =>
	DirectApi.Fetch_Raw(endpoint).then(async (response: Response) => {
		// QuickBooks answers 404 rather than an empty list when a connected company has no customers.
		if (response.status === 404) {
			return [];
		}
		const body = await response.json().catch(() => null);
		if (!response.ok || !Array.isArray(body)) {
			throw new ContactsFetchError(integration, body?.correlationId);
		}
		return defaultContactsMapper(body, integration);
	});

const fetchXeroContacts = (): Promise<Contact[]> => fetchContacts('xero/allContacts', INVOICE_INTEGRATIONS.XERO);
const fetchQuickbooksContacts = (): Promise<Contact[]> =>
	fetchContacts('quickbooks/all_customers', INVOICE_INTEGRATIONS.QUICKBOOKS);
const fetchEconomicContacts = (): Promise<Contact[]> => fetchContacts('economic/all_customers', INVOICE_INTEGRATIONS.ECONOMIC);

type ContactsResult = {contacts: Contact[]} | {error: unknown};

/**
 * Fetches contacts only for the integrations the company has connected. A failing integration is
 * skipped so the healthy ones can still be used; it rejects only when every enabled integration
 * fails, with the first failure in Xero, QuickBooks, e-conomic order.
 */
export const fetchEnabledIntegrationContacts = (enabled: EnabledInvoiceIntegrations): Promise<Contact[]> => {
	const fetches = [
		enabled.newXeroEnabled ? fetchXeroContacts : null,
		enabled.quickbooksEnabled ? fetchQuickbooksContacts : null,
		enabled.economicEnabled ? fetchEconomicContacts : null,
	].filter((fetcher): fetcher is () => Promise<Contact[]> => fetcher !== null);

	return Promise.all(
		fetches.map(fetcher =>
			fetcher().then(
				(contacts): ContactsResult => ({contacts}),
				(error: unknown): ContactsResult => ({error})
			)
		)
	).then(results => {
		const succeeded = results.flatMap(result => ('contacts' in result ? [result.contacts] : []));
		const firstFailure = results.find((result): result is {error: unknown} => 'error' in result);
		if (firstFailure && succeeded.length === 0) {
			throw firstFailure.error;
		}
		return succeeded.flat();
	});
};

/**
 * Whether a contact belongs under a Bill From - the rule that puts a Bill To entity in a tab on Manage
 * Bill To entities. Shared with the client import so an imported client's Bill To lands in
 * the tab the Bill To page would have chosen.
 */
export const contactBelongsToBillFrom = (
	contact: Contact,
	billFrom?: {
		integration?: string;
		externalId?: string;
	} | null
): boolean =>
	(!contact.integrationInstanceId || contact.integrationInstanceId === billFrom?.externalId) &&
	contact.integration === billFrom?.integration;

export const filterContactsByIntegrationInstanceId = (
	contacts: Contact[],
	billFrom?: {
		integration?: string;
		externalId?: string;
	} | null
): Contact[] => contacts.filter(contact => contactBelongsToBillFrom(contact, billFrom));

export type BillFromRef = {id: string; integration: string; externalId: string};

export type ImportBillTo = {
	contactId: string;
	integrationInstanceId: string | null;
	billFromId: string;
	name: string;
	address: string;
	taxId: string;
};

type ImportSelection = {id: string; integrationInstanceId?: string | null};

/**
 * The Bill To each selected contact should get on import. A contact is skipped - and counted,
 * so the user can be told - when it is missing from `allContacts` (its integration's fetch failed),
 * when the selection matches several contacts, or when it does not belong under exactly one Bill From.
 */
export const resolveImportBillTos = (
	selections: ImportSelection[],
	integration: string,
	allContacts: Contact[],
	billFroms: readonly BillFromRef[]
): {billTos: ImportBillTo[]; skippedCount: number} => {
	const integrationContacts = allContacts.filter(contact => contact.integration === integration);
	const billTos: ImportBillTo[] = [];
	let skippedCount = 0;

	selections.forEach(selection => {
		// QuickBooks customer ids are only unique per realm, so a selection that names no instance and
		// matches several contacts is ambiguous and is skipped rather than guessed.
		const candidates = integrationContacts.filter(
			contact =>
				contact.id === String(selection.id) &&
				(!selection.integrationInstanceId || contact.integrationInstanceId === selection.integrationInstanceId)
		);
		const contact = candidates.length === 1 ? candidates[0] : undefined;
		const matches = contact ? billFroms.filter(billFrom => contactBelongsToBillFrom(contact, billFrom)) : [];
		if (!contact || matches.length !== 1) {
			skippedCount++;
			return;
		}
		billTos.push({
			contactId: contact.id,
			integrationInstanceId: contact.integrationInstanceId || null,
			billFromId: matches[0].id,
			name: contact.name,
			address: contact.address || '',
			taxId: contact.taxId || '',
		});
	});

	return {billTos, skippedCount};
};
